'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '@/components/shared/Link';
import { getPersonBySlug } from '@/lib/actions/people';
import { useTranslations } from '@/i18n/intl';
import { Linkedin, ArrowLeft } from 'lucide-react';
import { UploadedImage } from '@/components/shared/UploadedImage';
import { RichText } from '@/components/shared/RichText';
import { stripHtml, parseJsonField } from '@/lib/utils';
import NotFoundPage from './NotFoundPage';

export default function PersonDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const tLife = useTranslations('LifeAtSimisi');
  const [person, setPerson] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!slug) return;
    void getPersonBySlug(slug).then((data) => {
      if (!data) {
        setNotFound(true);
      } else {
        setPerson(data);
      }
      setReady(true);
    });
  }, [slug]);

  if (!ready) return null;
  if (notFound || !person) return <NotFoundPage />;

  const experience = parseJsonField<string[]>(person.experience, []);
  const education = parseJsonField<string[]>(person.education_certifications, []);
  const expertise = parseJsonField<string[]>(person.expertise, []);

  return (
    <>
      <section className="bg-navy pt-[120px] md:pt-[140px] pb-12 md:pb-16 px-5 md:px-[40px]">
        <div className="max-w-[1170px] mx-auto text-left">
          <Link
            href="/about-us#our-team"
            className="inline-flex items-center gap-2 text-[12px] font-bold text-white/50 hover:text-white transition-colors mb-8 uppercase tracking-[0.2em] group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Our People
          </Link>
          <div className="max-w-[700px]">
            <h1 className="text-[32px] md:text-[48px] font-heading font-normal text-white mb-4">
              {person.name}
            </h1>
            <p className="text-[16px] md:text-[18px] text-white/80 font-sans leading-relaxed">
              {stripHtml(person.position || '')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-5 md:px-[40px] bg-white">
        <div className="max-w-[1170px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-24">
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-32">
                <div className="bg-white rounded-2xl shadow-xl shadow-navy/5 border border-gray-100 overflow-hidden">
                  <div className="aspect-[4/5] bg-gray-100 relative">
                    <UploadedImage
                      src={person.image || '/images/simisi/placeholder.png'}
                      alt={person.name}
                      fill
                      className="object-cover object-top"
                    />
                  </div>
                  <div className="p-6">
                    <h2 className="text-[18px] font-bold text-navy leading-tight mb-1 font-heading">
                      {person.name}
                    </h2>
                    <p className="text-[13px] text-gray-500 mb-6 font-sans">
                      {stripHtml(person.position || '')}
                    </p>

                    {person.linkedin_url && (
                      <a
                        href={person.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[13px] font-bold text-navy hover:text-primary transition-colors group"
                      >
                        <Linkedin size={16} className="text-[#0077B5]" />
                        <span className="underline underline-offset-4 decoration-1 group-hover:decoration-primary transition-all">
                          LinkedIn
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              {person.bio && (
                <div className="mb-16">
                  <h3 className="text-[15px] font-bold text-primary tracking-[0.2em] uppercase mb-8 pb-2 border-b border-gray-100">
                    Professional Summary
                  </h3>
                  <RichText
                    html={person.bio}
                    className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed font-sans font-light [&_p]:mb-4"
                  />
                </div>
              )}

              {expertise.length > 0 && (
                <div className="mb-16">
                  <h3 className="text-[15px] font-bold text-primary tracking-[0.2em] uppercase mb-8 pb-2 border-b border-gray-100">
                    Areas of Expertise
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    {expertise.map((item: string) => (
                      <div key={item} className="flex items-center gap-3 text-[15px] text-gray-600 py-1 font-sans">
                        <div className="w-1 h-1 bg-primary rounded-full shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {experience.length > 0 && (
                <div className="mb-16">
                  <h3 className="text-[15px] font-bold text-primary tracking-[0.2em] uppercase mb-8 pb-2 border-b border-gray-100">
                    Professional Experience
                  </h3>
                  <ul className="space-y-4">
                    {experience.map((item: string, index: number) => (
                      <li key={index} className="flex gap-4 items-start">
                        <div className="mt-2.5 shrink-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        </div>
                        <p className="text-[15px] md:text-[16px] text-gray-700 leading-relaxed font-sans">
                          {item}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {education.length > 0 && (
                <div className="mb-16">
                  <h3 className="text-[15px] font-bold text-primary tracking-[0.2em] uppercase mb-8 pb-2 border-b border-gray-100">
                    Education & Certifications
                  </h3>
                  <ul className="space-y-4">
                    {education.map((item: string, index: number) => (
                      <li key={index} className="flex gap-4 items-start">
                        <div className="mt-2.5 shrink-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        </div>
                        <p className="text-[15px] md:text-[16px] text-gray-700 leading-relaxed font-sans">
                          {item}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-navy">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-[32px] md:text-[40px] font-bold text-white leading-snug mb-6 font-heading">
            {tLife('cta.title')}
          </h2>
          <p className="text-[16px] text-white/80 leading-relaxed mb-8 font-sans">
            {tLife('cta.description')}
          </p>
          <Link
            href="/life-at-simisi#positions"
            className="inline-flex items-center bg-primary text-white text-[14px] font-semibold px-[25px] py-[11px] rounded-[25px] hover:bg-white hover:text-navy transition-all duration-300"
          >
            {tLife('cta.button')}
          </Link>
        </div>
      </section>
    </>
  );
}
